import { useCallback, useEffect, useRef, useState } from 'react';
import {
  generateWeeklyPlan,
  getFeedback,
  getWeeklyPlan,
  Feedback,
  WeeklyTrainingPlan
} from '../api/client';
import { AIFeedback } from '../components/AIFeedback';
import { FeedbackSkeleton } from '../components/Skeleton';
import { formatLongDate } from '../lib/date';
import { haptic } from '../lib/telegram';
import './AIAssistant.css';

interface AIAssistantProps {
  userId: number;
  onNavigate: (page: string) => void;
}

const DAY_NAMES = ['вс', 'пн', 'вт', 'ср', 'чт', 'пт', 'сб'];
const typeLabel = {
  run: 'Бег',
  recovery: 'Восстановление',
  strength: 'Силовая',
  rest: 'Отдых'
};

export const AIAssistant: React.FC<AIAssistantProps> = ({ userId }) => {
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [plan, setPlan] = useState<WeeklyTrainingPlan | null>(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoRefreshStarted = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [history, savedPlan] = await Promise.all([
        getFeedback(userId, 15),
        getWeeklyPlan(userId)
      ]);
      const unique = new Map<string, Feedback>();
      history
        .filter(item => item.feedback_type === 'workout_analysis')
        .forEach(item => {
          const key = item.workout_id ? `workout-${item.workout_id}` : JSON.stringify(item.feedback_text);
          if (!unique.has(key)) unique.set(key, item);
        });
      setFeedbacks([...unique.values()].slice(0, 8));
      setPlan(savedPlan);
    } catch (err: any) {
      setError(err?.message || 'Не удалось загрузить данные тренера');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) load();
  }, [userId, load]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    haptic.medium();
    try {
      setPlan(await generateWeeklyPlan(userId));
      haptic.success();
    } catch (err: any) {
      setError(err?.message || 'Не удалось составить недельный план');
      haptic.error();
    } finally {
      setGenerating(false);
    }
  };

  useEffect(() => {
    if (!loading && plan?.needsRefresh && !generating && !autoRefreshStarted.current) {
      autoRefreshStarted.current = true;
      handleGenerate();
    }
  // Автоматическая адаптация запускается один раз после появления новой тренировки.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, plan?.needsRefresh]);

  return (
    <main className="ai-assistant">
      <header className="ai-assistant-header">
        <span>RUN LAB / COACH</span>
        <h1>Тренировочный разбор</h1>
        <p>Персональная неделя на основе темпа, пульса, нагрузки и восстановления из ваших отчётов.</p>
      </header>

      <section className="coach-panel">
        <div className="coach-panel-top">
          <div>
            <span className="section-index">ПЛАН / 7 ДНЕЙ</span>
            <h2>{plan?.title || 'Следующий тренировочный цикл'}</h2>
          </div>
          {plan && <span className="plan-period">{plan.startDate.slice(5).replace('-', '.')}—{plan.endDate.slice(5).replace('-', '.')}</span>}
        </div>

        {loading ? (
          <div className="plan-loading"><span /> Анализируем историю</div>
        ) : (
          <>
            <p className="coach-summary">
              {plan?.summary || 'Загрузите хотя бы один отчёт, затем тренер соберёт сбалансированный план на ближайшие семь дней.'}
            </p>
            <button className="generate-week-btn" onClick={handleGenerate} disabled={generating}>
              <span>{generating ? 'Формируем план — это может занять до минуты' : plan ? 'Пересчитать неделю' : 'Составить план на 7 дней'}</span>
              <b>{generating ? '···' : '→'}</b>
            </button>
          </>
        )}
      </section>

      {error && <div className="coach-error">{error}</div>}
      {plan?.needsRefresh && generating && (
        <div className="adaptive-plan-note">Новая тренировка учтена. AI перестраивает оставшуюся неделю.</div>
      )}

      {plan && (
        <section className="week-plan">
          <div className="section-heading">
            <span className="section-index">РАСПИСАНИЕ</span>
            <h2>Неделя по дням</h2>
          </div>
          <div className="week-days">
            {plan.days.map(day => {
              const date = new Date(`${day.date}T12:00:00`);
              return (
                <details className={`week-day type-${day.type}`} key={day.date}>
                  <summary>
                    <div className="day-date">
                      <strong>{date.getDate()}</strong>
                      <span>{DAY_NAMES[date.getDay()]}</span>
                    </div>
                    <div className="day-content">
                    <div className="day-eyebrow">
                      <span>{typeLabel[day.type]}</span>
                      <span>{day.intensity}</span>
                    </div>
                    <h3>{day.title}</h3>
                    <p>{day.focus}</p>
                    {(day.durationMinutes || day.distanceKm) && (
                      <div className="day-metrics">
                        {day.durationMinutes && <span>{day.durationMinutes} мин</span>}
                        {day.distanceKm && <span>{day.distanceKm} км</span>}
                      </div>
                    )}
                    </div>
                    <i />
                  </summary>
                  <div className="day-details">
                    <ul>{day.details.map((detail, index) => <li key={index}>{detail}</li>)}</ul>
                  </div>
                </details>
              );
            })}
          </div>
          {plan.coachNotes?.length > 0 && (
            <details className="coach-notes">
              <summary><span className="section-index">ЗАМЕТКИ ТРЕНЕРА</span><i /></summary>
              <div>{plan.coachNotes.map((note, index) => <p key={index}>{note}</p>)}</div>
            </details>
          )}
        </section>
      )}

      <details className="feedback-section feedback-disclosure">
        <summary><div><span className="section-index">ИСТОРИЯ</span><h2>Разборы тренировок</h2><p>{feedbacks.length} последних без повторов</p></div><i /></summary>
        {loading ? (
          <div className="feedback-list">{Array.from({ length: 2 }).map((_, i) => <FeedbackSkeleton key={i} />)}</div>
        ) : feedbacks.length === 0 ? (
          <p className="empty-copy">После загрузки отчёта здесь появится анализ динамики и техники.</p>
        ) : (
          <div className="feedback-list">
            {feedbacks.map(feedback => (
              <article key={feedback.id} className="feedback-item">
                <div className="feedback-meta">
                  <span>{formatLongDate(feedback.created_at.split('T')[0])}</span>
                  <span>Анализ тренировки</span>
                </div>
                <AIFeedback feedback={feedback.feedback_text} />
              </article>
            ))}
          </div>
        )}
      </details>
    </main>
  );
};
